import React, { useState, useEffect } from "react";
import "./WorkPackageTable.css";
import { reviseSection } from "./api";
import { isProjectUnder90Days } from "./utils"; 


const WorkPackageTable = ({ workPackage, project_idx, project_start_date, onSave, onApprove, onDisprove }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localWP, setLocalWP] = useState({ ...workPackage });
  const [saveStatus, setSaveStatus] = useState("");

  const [showTooltip, setShowTooltip] = useState(false);
  const [showReviseBubble, setShowReviseBubble] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [selectedSection, setSelectedSection] = useState("");
  const sectionKeys = {"explanation": "WP", "activities": "SUBTASKS", "experiments": "EXPERIMENTS", "outcomes": "OUTCOMES"};

  useEffect(() => {
  setLocalWP({ ...workPackage });
  }, [workPackage]);

  const autoGrow = (element) => {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = element.scrollHeight + "px";
  };

  const handleChange = (key, value) => {
    setLocalWP({ ...localWP, [key]: value });
  };

  const handleListChange = (key, index, value) => {
    const updatedList = [...localWP[key]];
    updatedList[index] = value;
    setLocalWP({ ...localWP, [key]: updatedList });
  };

  const handleNestedChange = (key, index, field, value) => {
    const updated = [...localWP[key]];
    updated[index][field] = value;
    setLocalWP({ ...localWP, [key]: updated });
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setLocalWP({ ...workPackage }); // revert
    } else if (onDisprove) {
      onDisprove(workPackage);
    }
    setIsEditing((prev) => !prev);
  };

  const handleSave = async () => {
    setIsEditing(false);
    const result = await onSave(localWP);
    setSaveStatus(result === "SUCCESS" ? "Kaydedildi ✅" : "Kaydetme başarısız ❌");
    setTimeout(() => setSaveStatus(""), 3000);
  };

  const handleApprove = async () => {
    if (onApprove) {
      const success = await onApprove(localWP);
      alert(success ? "Onaylandı ✅" : "Onay başarısız ❌");
    }
  };

  const handleRevise = () => {
    setShowReviseBubble(true);
    setAiPrompt("");
    setAiResponse("");
    setSelectedSection("");
  };

  const handleSubmitPrompt = async () => {
    if (!aiPrompt.trim() || !selectedSection) return;

    setIsLoadingAI(true);
    setAiResponse("");
  
    try {
      const fullPrompt = JSON.stringify({
        work_package_title: localWP.work_package_title,
        user_prompt: aiPrompt,
      });
      // alert(fullPrompt);
      const revised = await reviseSection(project_idx, sectionKeys[selectedSection], fullPrompt);
      // alert(JSON.stringify(revised));
  
      setAiResponse(revised);
    } catch (error) {
      console.error("AI revision failed:", error);
      setAiResponse("Beklenmedik bir hata oluştu. Lütfen, daha sonra tekrar deneyin.");
    } finally {
      setIsLoadingAI(false);
    }
  };

  const applyAIResponse = () => {
    if (!aiResponse || !selectedSection) return;
  
    try {
      const parsed = typeof aiResponse === "string" ? JSON.parse(aiResponse) : aiResponse;
  
      // Find the WP in the AI response matching the current localWP title
      const matchingWP = parsed.find(
        (wp) => wp.work_package_title === localWP.work_package_title
      );
  
      if (!matchingWP) {
        alert("Bu iş paketi için Yapay Zeka çıktısı bulunamadı.");
        return;
      }
  
      const sectionData = matchingWP[selectedSection];
      let updatedValue;
  
      switch (selectedSection) {
        case "activities":
          updatedValue = Array.isArray(sectionData) ? sectionData : [];
          break;
  
        case "explanation":
          updatedValue = typeof sectionData === "string" ? sectionData : "";
          break;
  
        case "experiments":
          updatedValue = Array.isArray(sectionData)
            ? sectionData.map(exp => ({
                name: exp.name || "",
                reason: exp.reason || "",
              }))
            : [];
          break;
  
        case "outcomes":
          updatedValue = Array.isArray(sectionData)
            ? sectionData.map(out => ({
                name: out.name || "",
                due_date: out.due_date || "",
                used_in: out.used_in || "",
              }))
            : [];
          break;
  
        default:
          console.warn(`Unhandled section: ${selectedSection}`);
          return;
      }
  
      setLocalWP({ ...localWP, [selectedSection]: updatedValue });
    } catch (err) {
      console.error("Failed to apply AI response:", err);
      alert("Yapay Zeka çıktısı işlenemedi.");
    }
  
    setShowReviseBubble(false);
  };
  

  const sectionLabels = {
    activities: "Faaliyetler",
    explanation: "Açıklama",
    experiments: "Deney / Testler",
    outcomes: "Ara Çıktılar",
  };

  return (
    <div className="work-package-section">
      <div className="wps-header">
        <div className="wps-title-row">
          <h3 className="wps-title">
            İş Paketi:&nbsp;
            {isEditing ? (
                <input
                  type="text"
                  value={localWP.work_package_title}
                  onChange={(e) => handleChange("work_package_title", e.target.value)}
                  className="wp-title-input"
                />
            ) : (
              localWP.work_package_title
            )}
          </h3>
        </div>

        <div className="wp-meta-fields">
          <div className="wp-meta-field">
            <strong>Başlangıç Ayı:</strong>&nbsp;
            {isEditing ? (
                <input
                  type="number"
                  value={localWP.start_month}
                  onChange={(e) => handleChange("start_month", e.target.value)}
                  className="wp-meta-input"
                />
            ) : (
              <span className="wp-meta-static">{localWP.start_month || "—"}</span>
            )}
          </div>

          <div className="wp-meta-field">
            <strong>Süre (ay):</strong>&nbsp;
            {isEditing ? (
              <>
                <input
                  type="number"
                  value={localWP.duration}
                  onChange={(e) => handleChange("duration", e.target.value)}
                  className="wp-meta-input"
                />
                <span className="hint-wrapper">
                  ℹ️
                  <span className="hint-bubble">Başlık, Başlangıç ve Süre düzenlemeleri sonrası KAYDET butonuna basmayı unutmayın!</span>
                </span>
              </>
            ) : (
              <span className="wp-meta-static">{localWP.duration || "—"}</span>
            )}
          </div>
          {(() => {
            const startMonthOffset = parseInt(localWP.start_month || 1, 10) - 1; // 1-based index
            const duration = parseInt(localWP.duration || 0, 10);

            const projectDate = new Date(project_start_date);

            // Get start date: first day of the (startMonthOffset-th) month after project start
            const startDate = new Date(Date.UTC(projectDate.getUTCFullYear(), projectDate.getUTCMonth() + startMonthOffset, 1));

            // Get end date: last day of the (start + duration - 1)-th month
            const endDate = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + duration, 1));
            endDate.setUTCDate(0); // roll back to last day of target month

            const formatDate = (date) => date.toISOString().split('T')[0];

            return (
              <div className="wp-meta-field">
                <strong>Başlangıç / Bitiş Tarihi:</strong>&nbsp;
                <span className="wp-meta-static">{formatDate(startDate)} / {formatDate(endDate)}</span>
              </div>
            );
          })()}

        </div>


      </div>


      <table className="work-package-table">
        <tbody>

          <tr>
            <th>Faaliyetler</th>
            <td>
              <ol className="wps-list">
                {localWP.activities?.map((activity, idx) => (
                  <li key={idx}>
                    {isEditing ? (
                      <>
                        <textarea
                          value={activity}
                          onChange={(e) => {
                            handleListChange("activities", idx, e.target.value);
                            autoGrow(e.target);
                          }}
                          ref={(el) => el && autoGrow(el)}
                        />
                        <button
                          onClick={() => {
                              const updated = [...localWP.activities];
                              updated.splice(idx, 1);
                              setLocalWP({ ...localWP, activities: updated });
                          }}
                          className="delete-btn small"
                        >❌</button>
                      </>
                    ) : (
                      activity
                    )}
                  </li>
                ))}
              </ol>
              {isEditing && (
                
                <div className="add-row-container">
                  <button onClick={() =>
                    setLocalWP({ ...localWP, activities: [...(localWP.activities || []), ""] })
                  } className="add-row-btn">➕ Faaliyet Ekle</button>
                </div>
              )}
            </td>
          </tr>

          <tr>
            <th>Açıklama</th>
            <td>
              {isEditing ? (
                <textarea
                  value={localWP.explanation}
                  onChange={(e) => {
                    handleChange("explanation", e.target.value);
                    autoGrow(e.target);
                  }}
                  ref={(el) => el && autoGrow(el)}
                />
              ) : (
                localWP.explanation
              )}
            </td>
          </tr>
          
          <tr>
            <th>Deney / Testler</th>
            <td>
              {localWP.experiments?.length > 0 || isEditing ? (
                <table className="wps-inner-exp-table">
                  <thead>
                    <tr>
                      <th>Deney / Test Adı</th>
                      <th>Yapılma Nedeni</th>
                      {isEditing && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {localWP.experiments.map((exp, idx) => (
                      <tr key={idx}>
                        <td>
                          {isEditing ? (
                            <textarea
                              value={exp.name}
                              onChange={(e) =>
                                handleNestedChange("experiments", idx, "name", e.target.value)
                              }
                              ref={(el) => el && autoGrow(el)}
                            />
                          ) : (
                            exp.name
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <textarea
                              value={exp.reason}
                              onChange={(e) =>
                                handleNestedChange("experiments", idx, "reason", e.target.value)
                              }
                              ref={(el) => el && autoGrow(el)}
                            />
                          ) : (
                            exp.reason
                          )}
                        </td>
                        {isEditing && (
                          <td className="delete-col">
                            <button onClick={() => {
                              const updated = [...localWP.experiments];
                              updated.splice(idx, 1);
                              setLocalWP({ ...localWP, experiments: updated });
                            }} className="delete-btn small">❌</button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {isEditing && (
                      <tr>
                        <td colSpan="3">
                        <div className="add-row-container">
                          <button onClick={() => {
                            const updated = [...localWP.experiments, { name: "", reason: "" }];
                            setLocalWP({ ...localWP, experiments: updated });
                          }} className="add-row-btn">➕ Deney Ekle</button>
                        </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <em>Bu iş paketinde deney/test/analiz tanımlanmamıştır.</em>
              )}
            </td>
          </tr>

          <tr>
            <th>Ara Çıktılar</th>
            <td>
              <table className="wps-inner-table">
                <thead>
                  <tr>
                    <th>Çıktı</th>
                    <th>Beklenen Ay</th>
                    <th>Beklenen Tarih</th>
                    <th>Kullanılacak İP'ler</th>
                    {isEditing && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {localWP.outcomes.map((outcome, idx) => {
                    let outcomeDateDisplay = "—";
                    const dueMonth = parseInt(outcome.due_date || 0, 10);

                    if (!isNaN(dueMonth) && project_start_date) {
                      const base = new Date(project_start_date);
                      base.setMonth(base.getMonth() + dueMonth);
                      outcomeDateDisplay = base.toISOString().split("T")[0];
                    }

                    return (
                      <tr key={idx}>
                        <td>
                          {isEditing ? (
                            <textarea
                              value={outcome.name}
                              onChange={(e) =>
                                handleNestedChange("outcomes", idx, "name", e.target.value)
                              }
                              ref={(el) => el && autoGrow(el)}
                            />
                          ) : (
                            outcome.name
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              value={outcome.due_date}
                              onChange={(e) =>
                                handleNestedChange("outcomes", idx, "due_date", e.target.value)
                              }
                            />
                          ) : (
                            outcome.due_date || "—"
                          )}
                        </td>
                        <td>{isEditing ? "—" : outcomeDateDisplay}</td>
                        <td>
                          {isEditing ? (
                            <textarea
                              value={outcome.used_in}
                              onChange={(e) =>
                                handleNestedChange("outcomes", idx, "used_in", e.target.value)
                              }
                              ref={(el) => el && autoGrow(el)}
                            />
                          ) : (
                            outcome.used_in || "—"
                          )}
                        </td>
                        {isEditing && (
                          <td className="delete-col">
                            <button onClick={() => {
                              const updated = [...localWP.outcomes];
                              updated.splice(idx, 1);
                              setLocalWP({ ...localWP, outcomes: updated });
                            }} className="delete-btn small">❌</button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="btn-group">
        <button className="edit-btn" onClick={handleEditToggle}>
          {isEditing ? "VAZGEÇ" : "DÜZELT (+AI)"}
        </button>
        <button className="save-btn" onClick={handleSave} disabled={!isEditing}>
          KAYDET
        </button>
      </div>

      {saveStatus && <div className="save-status">{saveStatus}</div>}

      {isEditing && (
        <div style={{ position: "relative" }}>
          <button
            className="revise-fab"
            
            onClick={async () => {
              const isEligible = await isProjectUnder90Days(project_idx); // ← project_id
              if (!isEligible) {
                alert("Bu proje 90 günden eski. Yapay Zeka revizyonu artık kullanılamaz. Eğer Yapay Zeka revizyon özelliğini uzatmaya ihtiyacınız varsa lütfen bizimle iletişime geçin: info@ideatrum.com");
                return;
              }

              setShowReviseBubble((prev) => {
                const next = !prev;
                if (next) handleRevise(); // Only reset when opening
                return next;
              });
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            🧠 AI
          </button>
          {showTooltip && <div className="revise-fab-tooltip">YAPAY ZEKA İLE REVİZE ET</div>}

          {showReviseBubble && (
            <div className="revise-bubble">
              {!selectedSection ? (
                <>
                  <div style={{ marginBottom: "8px" }}>
                    Hangi bölümü revize etmek istersiniz?
                  </div>
                  <div className="revise-btn-group">
                    <button onClick={() => setSelectedSection("activities")}>Faaliyetler</button>
                    <button onClick={() => setSelectedSection("explanation")}>Açıklama</button>
                    <button onClick={() => setSelectedSection("experiments")}>Deneyler</button>
                    <button onClick={() => setSelectedSection("outcomes")}>Ara Çıktılar</button>
                  </div>
                </>
              ) : !aiResponse ? (
                <>
                  <textarea
                    placeholder={`"${sectionLabels[selectedSection]}" için ProWA'dan ne yapmasını istediğinizi yazın...`}
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                  />
                  <button
                    className="revise-submit-btn"
                    onClick={handleSubmitPrompt}
                    disabled={isLoadingAI}
                  >
                    {isLoadingAI ? "Revize ediliyor..." : "Gönder"}
                  </button>
                </>
              ) : (
                <>
                  {(() => {
                      try {
                        const parsed = typeof aiResponse === "string" ? JSON.parse(aiResponse) : aiResponse;
                        const matchingWP = parsed.find(
                          (wp) => wp.work_package_title === localWP.work_package_title
                        );
                        const sectionData = matchingWP?.[selectedSection];

                        if (!sectionData) return <div className="revise-message">Bu iş paketi için veri bulunamadı.</div>;

                        switch (selectedSection) {
                          case "activities":
                            return (
                              <div className="revise-message">
                                <ol>
                                  {sectionData.map((item, idx) => (
                                    <li key={idx}>{item}</li>
                                  ))}
                                </ol>
                              </div>
                            );
                          case "explanation":
                            return <div className="revise-message">{sectionData}</div>;
                          case "experiments":
                            return (
                              <div className="revise-message">
                                {sectionData.map((exp, idx) => (
                                  <div key={idx}>
                                    <strong>{exp.name}</strong>: {exp.reason}
                                  </div>
                                ))}
                              </div>
                            );
                          case "outcomes":
                            return (
                              <div className="revise-message">
                                {sectionData.map((out, idx) => (
                                  <div key={idx}>
                                    <strong>{out.name}</strong><br />
                                    Tarih: {out.due_date}<br />
                                    IP: {out.used_in}<br /><br />
                                  </div>
                                ))}
                              </div>
                            );
                          default:
                            return <div className="revise-message">Veri işlenemedi.</div>;
                        }
                      } catch (err) {
                        return <div className="revise-message">Yanıt çözümlenemedi ❌</div>;
                      }
                    })()}
                  <div className="revise-btn-group">
                    <button onClick={applyAIResponse}>KULLAN</button>
                    <button onClick={() => setShowReviseBubble(false)}>VAZGEÇ</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkPackageTable;
